import type { ToolDefinition } from "../types.js";

/**
 * Generate TypeScript interfaces from tool definitions.
 */
export function generateTypes(tools: ToolDefinition[], appName: string): string {
  const lines: string[] = [];
  const mapName = `${toPascalCase(appName)}ToolMap`;

  lines.push("/**");
  lines.push(` * Auto-generated by @nimblebrain/synapse codegen`);
  lines.push(` * Source: ${appName}`);
  lines.push(` *`);
  lines.push(
    ` * DO NOT EDIT — regenerate with: npx synapse codegen --from-manifest ./manifest.json`,
  );
  lines.push(` */`);
  lines.push("");

  const mapEntries: string[] = [];

  for (const tool of tools) {
    const baseName = toPascalCase(tool.name);

    // Input type
    const inputName = `${baseName}Input`;
    lines.push(schemaToInterface(inputName, tool.inputSchema));
    lines.push("");

    // Output type
    let outputName = "unknown";
    if (tool.outputSchema) {
      outputName = `${baseName}Output`;
      lines.push(schemaToInterface(outputName, tool.outputSchema));
      lines.push("");
    }

    mapEntries.push(`  ${tool.name}: { input: ${inputName}; output: ${outputName} };`);
  }

  // Tool map
  lines.push(`/** Tool type map for typed useCallTool */`);
  lines.push(`export interface ${mapName} {`);
  for (const entry of mapEntries) {
    lines.push(entry);
  }
  lines.push(`}`);
  lines.push("");

  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// JSON Schema -> TypeScript conversion
// ---------------------------------------------------------------------------

function schemaToInterface(name: string, schema: Record<string, unknown>): string {
  const type = schemaToType(schema, name);

  // If the schema is an object, generate a named interface
  if (schema.type === "object" || schema.properties) {
    return generateInterface(name, schema);
  }

  // Otherwise, generate a type alias
  return `export type ${name} = ${type};`;
}

function generateInterface(name: string, schema: Record<string, unknown>): string {
  const lines: string[] = [];
  lines.push(`export interface ${name} {`);

  const props = (schema.properties ?? {}) as Record<string, Record<string, unknown>>;
  const required = new Set((schema.required ?? []) as string[]);

  for (const [key, propSchema] of Object.entries(props)) {
    const isRequired = required.has(key);
    const type = schemaToType(propSchema, name + toPascalCase(key));
    const desc = propSchema.description;
    if (desc) {
      lines.push(`  /** ${desc} */`);
    }
    lines.push(`  ${key}${isRequired ? "" : "?"}: ${type};`);
  }

  lines.push(`}`);
  return lines.join("\n");
}

function schemaToType(schema: Record<string, unknown>, context: string): string {
  // Handle enum
  if (schema.enum && Array.isArray(schema.enum)) {
    return (schema.enum as unknown[])
      .map((v) => (typeof v === "string" ? `"${v}"` : String(v)))
      .join(" | ");
  }

  // Handle oneOf / anyOf
  if (schema.oneOf && Array.isArray(schema.oneOf)) {
    return (schema.oneOf as Record<string, unknown>[])
      .map((s, i) => schemaToType(s, `${context}Option${i}`))
      .join(" | ");
  }
  if (schema.anyOf && Array.isArray(schema.anyOf)) {
    return (schema.anyOf as Record<string, unknown>[])
      .map((s, i) => schemaToType(s, `${context}Option${i}`))
      .join(" | ");
  }

  // Handle allOf (intersection)
  if (schema.allOf && Array.isArray(schema.allOf)) {
    return (schema.allOf as Record<string, unknown>[])
      .map((s, i) => schemaToType(s, `${context}Part${i}`))
      .join(" & ");
  }

  // Handle type-based conversion
  const type = schema.type as string | string[] | undefined;

  if (Array.isArray(type)) {
    // Multi-type (e.g., ["string", "null"])
    return type.map((t) => primitiveType(t)).join(" | ");
  }

  switch (type) {
    case "string":
      return "string";
    case "number":
    case "integer":
      return "number";
    case "boolean":
      return "boolean";
    case "null":
      return "null";
    case "array": {
      const items = schema.items as Record<string, unknown> | undefined;
      if (items) {
        return `${schemaToType(items, `${context}Item`)}[]`;
      }
      return "unknown[]";
    }
    case "object": {
      if (schema.properties) {
        // Inline object — generate property types
        const props = schema.properties as Record<string, Record<string, unknown>>;
        const required = new Set((schema.required ?? []) as string[]);
        const fields = Object.entries(props)
          .map(([key, propSchema]) => {
            const t = schemaToType(propSchema, context + toPascalCase(key));
            return `${key}${required.has(key) ? "" : "?"}: ${t}`;
          })
          .join("; ");
        return `{ ${fields} }`;
      }
      return "Record<string, unknown>";
    }
    default:
      return "unknown";
  }
}

function primitiveType(t: string): string {
  switch (t) {
    case "string":
      return "string";
    case "number":
    case "integer":
      return "number";
    case "boolean":
      return "boolean";
    case "null":
      return "null";
    default:
      return "unknown";
  }
}

function toPascalCase(str: string): string {
  return str
    .split(/[-_@/]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");
}
